/**
 * "Completes the look" attach offer on the product page.
 *
 * The whole point of this module is reach: 74.5% of orders pay through an express wallet,
 * and a wallet tap on the PDP checks out the form's variant alone. Anything living in the
 * cart drawer never gets seen by those buyers. So this sits above the buy button.
 *
 * Shopify's payment_button cannot carry a second line item, so accepting the offer has to
 * go through the cart. While the offer is checked we hide the wallet button, the theme's
 * Add-to-cart and the sticky bar, and present one "Add both to cart" action instead.
 * Leaving the wallet live would silently drop the earrings the customer just accepted.
 *
 * Rendering decisions (which candidate, which metal) are recomputed on every variant
 * change from the data the Liquid emitted - the module holds no opinion of its own.
 */
/**
 * Per-tab id so impressions and accepts can be tied together without cookies or any
 * personal data. Nothing here identifies a shopper.
 */
function attachSessionId() {
  try {
    let id = sessionStorage.getItem('m_attach_sid');
    if (!id) {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('m_attach_sid', id);
    }
    return id;
  } catch (error) {
    return null;
  }
}

class ProductAttachUpsell extends HTMLElement {
  constructor() {
    super();

    this.rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.busy = false;
    this.selected = null;

    try {
      this.candidates = JSON.parse(this.dataset.candidates || '[]');
      this.productMetals = JSON.parse(this.dataset.productMetals || '{}');
    } catch (error) {
      this.candidates = [];
      this.productMetals = {};
    }

    this.toggle = this.querySelector('[data-pau-toggle]');
    this.addButton = this.querySelector('[data-pau-add]');
    this.message = this.querySelector('[data-pau-message]');
  }

  connectedCallback() {
    if (!this.candidates.length || !this.toggle) return;

    this.form = document.querySelector('product-form.m-product-form--main form') || document.querySelector('form[data-type="add-to-cart-form"]');

    // Relocate the accept button into the form's button row so it lands AFTER the quantity
    // selector, where a primary CTA belongs. The block itself has to sit above the buy
    // button (that is the whole point), but its button does not.
    const buttonRow = this.form && this.form.querySelector('.m-product-form--wrapper');
    if (buttonRow) buttonRow.appendChild(this.addButton);

    this.toggle.addEventListener('change', this.onToggle.bind(this));
    this.addButton.addEventListener('click', this.onAddBoth.bind(this));

    // The theme dispatches no variant-change event, so we watch for any option input
    // settling and then re-read the hidden input the variant picker keeps updated.
    this.onDocumentChange = () => window.setTimeout(() => this.render(), 0);
    document.addEventListener('change', this.onDocumentChange);

    this.render();
    if (!this.hidden) this.watchForImpression();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onDocumentChange);
    if (this.observer) this.observer.disconnect();
    this.restoreThemeButtons();
  }

  /**
   * Count an impression only when the card actually scrolls into view, once per page view.
   * Rendering it off-screen is not a impression, and inflating the denominator would make
   * the accept rate look worse than it is.
   */
  watchForImpression() {
    if (this.impressionSent || typeof IntersectionObserver === 'undefined') return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.impressionSent || this.hidden) return;
          this.impressionSent = true;
          this.track('attach_impression');
          this.observer.disconnect();
        });
      },
      { threshold: 0.5 }
    );
    this.observer.observe(this);
  }

  /**
   * Fire-and-forget telemetry. Wrapped so a logging failure can never break the offer -
   * a shopper losing their add-to-cart because an analytics call threw would be a far
   * worse outcome than a missing row.
   */
  track(eventName) {
    const url = this.dataset.eventsUrl;
    const key = this.dataset.eventsKey;
    // 'unset' is the schema default - telemetry is simply off until it is configured.
    if (!url || !key || url === 'unset' || key === 'unset' || url.indexOf('https://') !== 0) return;

    const selected = this.selected;
    try {
      fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          apikey: key,
          Authorization: `Bearer ${key}`,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal',
        },
        body: JSON.stringify({
          event: eventName,
          arm: this.dataset.arm || null,
          product_handle: this.dataset.productHandle || null,
          offer_handle: selected ? selected.candidate.handle : null,
          offer_variant_id: selected ? selected.variant.id : null,
          metal: this.currentMetal() || null,
          session_id: attachSessionId(),
        }),
      }).catch(() => {});
    } catch (error) {
      /* telemetry is never worth an exception */
    }
  }

  /** Metal currently chosen on the necklace, resolved through the form's hidden variant id. */
  currentMetal() {
    const input = this.form && this.form.querySelector('input[name="id"]');
    const fromForm = input && this.productMetals[String(input.value)];
    return fromForm || this.dataset.currentMetal || '';
  }

  /**
   * First candidate with an available variant in the selected metal.
   * Order in the theme editor is the priority order - no scoring, no surprises.
   */
  resolve(metal) {
    for (let i = 0; i < this.candidates.length; i += 1) {
      const candidate = this.candidates[i];
      const variant = candidate.variants.find((v) => v.metal === metal && v.available);
      if (variant) return { candidate, variant };
    }
    return null;
  }

  render() {
    const match = this.resolve(this.currentMetal());

    // No candidate in this metal - hide rather than offer something they did not choose.
    if (!match) {
      this.selected = null;
      this.hidden = true;
      if (this.toggle.checked) {
        this.toggle.checked = false;
        this.applyToggleState(true);
      }
      return;
    }

    const changed = !this.selected || this.selected.variant.id !== match.variant.id;
    this.selected = match;
    this.hidden = false;

    if (!changed) return;

    const image = this.querySelector('[data-pau-image]');
    if (image && match.candidate.image) {
      image.src = match.candidate.image;
      image.alt = match.candidate.title;
    }

    const title = this.querySelector('[data-pau-title]');
    if (title) title.textContent = match.candidate.title;

    const price = this.querySelector('[data-pau-price]');
    if (price) price.textContent = `+${match.variant.price}`;
  }

  /** User clicked the checkbox. Only this path is worth counting. */
  onToggle() {
    this.applyToggleState(false);
  }

  /**
   * @param {boolean} silent - true when we flip the checkbox ourselves (resetting after an
   *   add, or hiding because no candidate matches the chosen metal). Those are not shopper
   *   decisions and must not land in the telemetry, or the accept rate is nonsense.
   */
  applyToggleState(silent) {
    const on = this.toggle.checked;

    this.classList.toggle('pau--active', on);
    this.addButton.hidden = !on;
    this.setMessage('');

    if (on) {
      this.hideThemeButtons();
      if (!silent) this.track('attach_accept');
    } else {
      this.restoreThemeButtons();
      if (!silent) this.track('attach_decline');
    }
  }

  /**
   * Every path that would check out the necklace alone, while the customer has asked for
   * both. Hidden by class so the theme keeps ownership of its own inline styles.
   */
  themeButtons() {
    const nodes = [];
    document.querySelectorAll('.m-product-dynamic-checkout').forEach((node) => nodes.push(node));
    document.querySelectorAll('.m-sticky-addtocart').forEach((node) => nodes.push(node));
    if (this.form) {
      const atc = this.form.querySelector('.m-add-to-cart');
      if (atc) nodes.push(atc);
    }
    return nodes;
  }

  hideThemeButtons() {
    this.themeButtons().forEach((node) => node.classList.add('pau-suppressed'));
  }

  restoreThemeButtons() {
    document.querySelectorAll('.pau-suppressed').forEach((node) => node.classList.remove('pau-suppressed'));
  }

  onAddBoth() {
    if (this.busy || !this.selected || !this.form) return;

    const input = this.form.querySelector('input[name="id"]');
    const mainId = input && Number(input.value);
    if (!mainId) {
      this.setMessage('Please choose an option first.');
      return;
    }

    const qtyInput = this.form.querySelector('input[name="quantity"]');
    const quantity = qtyInput && Number(qtyInput.value) > 0 ? Number(qtyInput.value) : 1;

    this.busy = true;
    this.classList.add('pau--loading');

    fetch(`${this.rootUrl}cart/add.js`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
      body: JSON.stringify({
        items: [
          { id: mainId, quantity: quantity },
          { id: this.selected.variant.id, quantity: 1, properties: { _attach_source: 'pdp' } },
        ],
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) throw new Error(data.description || data.message);
        return this.openCart();
      })
      .catch((error) => {
        this.setMessage(error.message || 'Something went wrong. Please try again.');
      })
      .finally(() => {
        this.busy = false;
        this.classList.remove('pau--loading');
      });
  }

  /** Mirrors cart-free-gift.js: let the theme re-render, then surface the drawer. */
  openCart() {
    return fetch(`${this.rootUrl}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        const drawer = document.querySelector('m-cart-drawer');

        if (!drawer) {
          window.location.href = (window.MinimogSettings && window.MinimogSettings.routes.cart) || `${this.rootUrl}cart`;
          return;
        }

        drawer.onCartDrawerUpdate();
        drawer.classList.toggle('m-cart--empty', cart.item_count === 0);

        if (window.MinimogEvents && window.MinimogTheme) {
          MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, cart);
        }

        // Call open() directly. The openCartDrawer pub/sub event is a no-op in this
        // theme - emitting it leaves the drawer shut, so the shopper sees nothing happen.
        if (typeof drawer.open === 'function') drawer.open();

        // The offer has been taken; reset so a second add is a deliberate choice.
        this.track('attach_add');
        this.toggle.checked = false;
        this.applyToggleState(true);
      });
  }

  setMessage(text) {
    if (this.message) this.message.textContent = text || '';
  }
}

if (!customElements.get('product-attach-upsell')) {
  customElements.define('product-attach-upsell', ProductAttachUpsell);
}
